import {
  doc, collection, addDoc, deleteDoc, getDoc, getDocs, setDoc, updateDoc, onSnapshot,
  query, where, orderBy, limit, serverTimestamp, increment, arrayUnion, arrayRemove, deleteField,
} from 'firebase/firestore';
import { db } from './firebase';
import { SHARE_KINDS, SHARE_TITLE_MAX, SHARE_THUMB_MAX_CHARS } from '../constants/share';
import { findProfanity } from '../utils/profanity';

// ── CHAT ─────────────────────────────────────────────────────────────────────
// One-to-one messaging between a visitor and a designer.
//
// ══ SCHEMA ══════════════════════════════════════════════════════════════════
//
//   chats/{chatId}
//     participants     string[2]   both uids, sorted — array-contains queries
//     participantInfo  { uid: { name, avatar } }
//     lastMessage      string      truncated preview
//     lastMessageAt    timestamp   what the list is ordered by
//     lastSenderUid    string      to show "you:" in the list
//     unread           { uid: n }  per-participant counter
//     createdAt        timestamp
//
//   chats/{chatId}/messages/{messageId}
//     senderUid, text, createdAt
//     share?  { kind, ownerUid, refId, title, thumb }   — see SHARE_KINDS
//
// ══ WHY THIS SHAPE, IN COST TERMS ═══════════════════════════════════════════
//
// THE CHAT ID IS DERIVED, NOT RANDOM: sorted(uidA, uidB).join('__'). Opening a
// conversation is therefore a direct document get — no "find a chat where
// participants contains both of us" query, which Firestore cannot express
// efficiently anyway. It also makes duplicate chats between the same pair
// impossible by construction rather than by convention. (The `likes`
// collection already uses this trick; same reasoning.)
//
// THE LIST IS SELF-CONTAINED. `lastMessage` and `participantInfo` are
// denormalised onto the chat document, so rendering a list of 20 conversations
// costs 20 documents — not 20 + 20 profile reads + 20 message reads. The name
// and picture are a snapshot taken when the chat opened; they are a label on a
// conversation, not a live mirror of someone's profile.
//
// MESSAGES ARE A SUBCOLLECTION, read newest-first with a LIMIT. A thread that
// runs to thousands of messages must never be a page load. History beyond the
// window is paged in on demand (loadOlder).
//
// UNREAD RIDES ALONG FREE. The chat document is written once per message
// anyway, to update the preview — so the counter costs no extra write.

export const MESSAGE_PAGE = 40;      // how many messages a thread opens with
export const MESSAGE_MAX = 2000;     // characters
const PREVIEW_MAX = 120;             // stored on the chat doc for the list

// What the conversation list shows for a picture with no caption. A literal
// string, not a data URL: the list renders dozens of these and the preview is
// on the CHAT document, which every list load reads.
const IMAGE_PREVIEW = '📷';
// The same idea for a shared piece of work: a glyph plus its title, so the row
// says WHAT was sent rather than only that something was.
const SHARE_PREVIEW = '🔗';

// ── SHARING WORK INTO A CONVERSATION ─────────────────────────────────────────
//
// Sending someone a design used to mean copying the public /share link out of
// the Share modal and pasting it into a message, where it arrived as a bare
// URL. This is that link as a first-class message: a card carrying the title
// and a small picture, which opens the real thing when tapped.
//
// The shape and its limits live in constants/share.js — the encoder needs the
// same numbers and must not import Firestore to get them.

/**
 * Where a share card goes when it is tapped.
 *
 * Every kind resolves to a route that ALREADY EXISTS, deliberately: a project
 * to its public share page, a gallery item to the deep link its owner's profile
 * already understands (the same one "someone liked your photo" arrives on). So
 * a card works for whoever receives it without a new page to maintain, and
 * without the recipient needing an account for the project case.
 */
export function shareHref(share, fromChatId = '') {
  if (!share?.ownerUid || !share?.refId) return '';
  // ── WHERE TO GO BACK TO ─────────────────────────────────────────────────
  // Opening a shared card navigates to a page, which closes the chat overlay
  // (it is state, not a route — see ChatUIContext). Closing the photo then left
  // you on a stranger's profile with the conversation gone, and on a phone no
  // obvious way back to it.
  //
  // The conversation's id rides along in the URL so the page can reopen it. In
  // the URL rather than in router state because a shared link is a real link:
  // opened in a new tab, or restored after a reload, state is gone and the
  // parameter is not.
  const back = fromChatId ? `from=${encodeURIComponent(fromChatId)}` : '';
  switch (share.kind) {
    case SHARE_KINDS.PROJECT: return `/share/${share.ownerUid}/${share.refId}`;
    // ── A PHOTO GOES TO ITS OWN PAGE ────────────────────────────────────────
    // It used to open the owner's profile with `?photo=` and the viewer sprung
    // over it, which for a shop's listing meant landing on the shop rather than
    // on the thing that was sent — and closing the picture stranded the reader
    // there.
    //
    // A SHARE RECORD CANNOT SAY WHETHER IT IS A LISTING. It carries kind,
    // owner, id, title and a thumbnail, and `SHARE_KINDS.PHOTO` covers both a
    // shop's product and a designer's work — the two are the same collection
    // and the price is not copied onto the card. So this always points at
    // /product/:id, and ProductPage sends a photo that turns out NOT to be a
    // listing on to the owner's profile instead. Deciding it there is what
    // makes it right for the cards ALREADY SENT, which no change here could
    // reach.
    case SHARE_KINDS.PHOTO:   return `/product/${share.refId}${back ? `?${back}` : ''}`;
    // A 3D design has no product page — it is work, not stock — so it still
    // opens on its owner's profile, where its scene and its thread live.
    case SHARE_KINDS.DESIGN:  return `/designer/${share.ownerUid}?design=${share.refId}${back ? `&${back}` : ''}`;
    default: return '';
  }
}

/**
 * Validates and trims what the caller wants to send, into exactly the shape the
 * rules accept.
 *
 * THE TITLE AND THE PICTURE ARE A SNAPSHOT, written by the sender — unlike
 * `replyTo`, which stores only an id precisely so a quote cannot lie. That is
 * not an oversight here, it is forced: a project is readable only once its
 * owner shares it, so resolving the card live would leave a blank rectangle for
 * anyone who receives one before (or instead of) that. Resolving it live would
 * also cost one extra document read per card, on every load of a thread that
 * contains one — which is the cost `participantInfo` and `lastMessage` are
 * denormalised to avoid everywhere else in this file.
 *
 * What that admits is a sender who mislabels their own link, which is no more
 * than they could do by typing a sentence. What it cannot do is put words under
 * the OTHER person's name: the card is plainly one message from one sender, and
 * `senderUid` is pinned to the auth token.
 *
 * An oversized thumbnail is DROPPED, not rejected. A card without a picture is
 * still a working card; a send that fails because a picture was 2KB too big is
 * not.
 */
export function normalizeShare(share) {
  if (!share) return null;
  const kind = share.kind;
  if (!Object.values(SHARE_KINDS).includes(kind)) throw new Error('bad-share-kind');

  const ownerUid = String(share.ownerUid || '').slice(0, 64);
  const refId = String(share.refId || '').slice(0, 64);
  if (!ownerUid || !refId) throw new Error('bad-share-ref');

  const thumb = String(share.thumb || '');
  return {
    kind,
    ownerUid,
    refId,
    title: trim(share.title, SHARE_TITLE_MAX),
    ...(thumb && thumb.length <= SHARE_THUMB_MAX_CHARS ? { thumb } : {}),
  };
}

// ── WHAT AN IMAGE COSTS ──────────────────────────────────────────────────────
// The inline preview is what every reader of the thread downloads, so it is
// sized for the bubble it appears in (max 560 CSS px) rather than for the
// screen. The full rendition sits in its own document and is fetched only when
// someone opens the picture. See sendMessage for the reasoning.
export const CHAT_IMAGE = {
  previewMaxPx: 800,
  previewQuality: 0.72,
  fullMaxPx: 1600,
  fullQuality: 0.82,
  fullTargetBytes: 320 * 1024,
  fullMaxBytes: 500 * 1024,
};
const CHAT_LIST_MAX = 40;

/**
 * The one and only id for a pair of users. Sorted so that A→B and B→A produce
 * the same string; without that you get two chats for one conversation and
 * neither side sees the other's messages.
 */
export function chatIdFor(uidA, uidB) {
  return [uidA, uidB].sort().join('__');
}

const chatRef = (chatId) => doc(db, 'chats', chatId);
const messagesRef = (chatId) => collection(db, 'chats', chatId, 'messages');

const trim = (s, max) => String(s || '').trim().slice(0, max);

/**
 * What the conversation list shows for a message.
 *
 * ONE PLACE, because two callers need it: sending a message writes the preview,
 * and deleting the newest one has to replace it with whatever is now last.
 * Those two had already drifted — a message with no caption showed 📷 when it
 * was sent and a BLANK row after the message above it was deleted, because only
 * the sending path knew that a picture is a message.
 */
export function previewOf(msg) {
  if (!msg) return '';
  const body = trim(msg.text, PREVIEW_MAX);
  if (body) return body;
  if (msg.share) return trim(`${SHARE_PREVIEW} ${msg.share.title || ''}`, PREVIEW_MAX);
  if (msg.image) return IMAGE_PREVIEW;
  return '';
}

/**
 * Returns the existing chat, or creates it.
 *
 * ONE read in the common case (the chat already exists) and one write the very
 * first time. `me` and `them` are {uid, name, avatar} — the caller already has
 * both on screen, so nothing needs looking up here.
 */
export async function openChat(me, them) {
  if (!me?.uid || !them?.uid) throw new Error('missing-participant');
  if (me.uid === them.uid) throw new Error('self-chat');

  const chatId = chatIdFor(me.uid, them.uid);
  const snap = await getDoc(chatRef(chatId));
  if (snap.exists()) {
    // DELIBERATELY OPENING A CONVERSATION UN-DELETES IT — for me only.
    //
    // Hiding takes the chat out of my list, and the list is where the UI reads
    // the other person's name and uid from. So reopening a deleted thread gave
    // a conversation with "Someone", no recipient, and a send button that could
    // not work: the chat existed, but the only place that described it had
    // filtered it out.
    //
    // Choosing to write to someone is the clearest possible statement that the
    // conversation belongs in my list again. The history stays gone — that is
    // `clearedAt`, which is untouched here.
    if ((snap.data().hiddenFor || []).includes(me.uid)) {
      await updateDoc(chatRef(chatId), { hiddenFor: arrayRemove(me.uid) });
      _unread = null;
    }
    return { id: chatId, ...snap.data() };
  }

  const fresh = {
    participants: [me.uid, them.uid].sort(),
    participantInfo: {
      [me.uid]:   { name: me.name || '',   avatar: me.avatar || '' },
      [them.uid]: { name: them.name || '', avatar: them.avatar || '' },
    },
    lastMessage: '',
    lastMessageAt: serverTimestamp(),
    lastSenderUid: '',
    unread: { [me.uid]: 0, [them.uid]: 0 },
    createdAt: serverTimestamp(),
  };
  await setDoc(chatRef(chatId), fresh);
  return { id: chatId, ...fresh };
}

/**
 * Sends a message: one write to the subcollection, one to the parent.
 *
 * The parent write is what keeps the conversation LIST cheap — without a
 * denormalised preview, rendering it would mean reading the newest message of
 * every chat.
 */
export async function sendMessage(chatId, senderUid, recipientUid, text, { replyTo = '', image = null, share = null } = {}) {
  const body = trim(text, MESSAGE_MAX);
  const card = normalizeShare(share);
  // An image IS the message when there is no caption. So is a shared design —
  // the caption on both is optional.
  if (!body && !image && !card) throw new Error('empty');

  // ── NOT SENT AT ALL ───────────────────────────────────────────────────────
  // Checked HERE rather than in the composer because there are two composers —
  // the thread and the caption on a shared card — and a rule enforced at each
  // call site is a rule that will be missed at the third one.
  //
  // THROWN, not silently redacted. A message that arrives with a word quietly
  // removed reads as a bug to the sender and as a non-sequitur to the reader;
  // a refusal tells the person exactly what happened while their sentence is
  // still in the box.
  //
  // THIS IS A BROWSER CHECK, and it stops typing rather than determination:
  // clients write to Firestore directly here, so anyone willing to open a
  // console can skip it. Making it binding needs the write to go through a
  // Cloud Function or a rule, which is a larger change than this one —
  // see the note in utils/profanity.
  const bad = findProfanity(body);
  if (bad) {
    // The offending word rides on the error so the UI can name it. Callers
    // switch on `.code`; the message text is for a developer reading a log.
    const err = new Error(`blocked word: ${bad}`);
    err.code = 'profanity';
    err.word = bad;
    throw err;
  }

  // ── IMAGES: TWO TIERS, AND THIS IS THE WHOLE COST ARGUMENT ────────────────
  //
  // Storage is not provisioned for this project, so pictures live in Firestore
  // as data URLs — the same choice the gallery makes. Firestore bills per
  // document read plus the bytes that come back, and a thread re-reads its
  // last fifty messages every time it is opened. So what goes ON the message
  // is not the picture the user chose:
  //
  //   message.image.url   ~800px, WebP  — typically 40–70KB. The one everybody
  //                                       downloads, sized for a bubble that is
  //                                       at most 560 CSS px wide.
  //   chats/{id}/media/{messageId}.url  ~1600px, up to ~400KB. Read ONLY when
  //                                       someone taps the picture to open it.
  //
  // Inlining the large one instead would multiply the cost of opening a
  // conversation by roughly eight, permanently, to serve a full-resolution
  // image that most people never ask for. Splitting them makes the common path
  // cheap and leaves the rare one exactly as expensive as it always was.
  //
  // WRITTEN IN THIS ORDER, message first: if the media write fails the picture
  // still sends and still displays — opening it just shows the preview instead
  // of the full rendition. The reverse order risks the opposite, a stored image
  // nothing points at.
  const ref = doc(messagesRef(chatId));

  await setDoc(ref, {
    senderUid,
    text: body,
    createdAt: serverTimestamp(),
    ...(image ? {
      image: {
        url: image.previewUrl,
        // Carried so the bubble can reserve the right space before the picture
        // decodes; without them the thread jumps as each one loads.
        w: image.width,
        h: image.height,
        // Whether a full rendition exists to fetch on tap.
        full: !!image.fullUrl,
      },
    } : {}),
    // A PIECE OF WORK, AS A CARD. See normalizeShare for why this one IS a
    // snapshot where the quote below deliberately is not.
    ...(card ? { share: card } : {}),
    // ONLY THE ID OF THE QUOTED MESSAGE — never a copy of its text.
    //
    // A snapshot would be written by the client, so anyone could send a reply
    // quoting words the other person never said, and it would render as their
    // message. Storing the id means the quote is always rendered from the real
    // document, which is already on screen; a reply to something outside the
    // loaded window simply shows as unavailable.
    ...(replyTo ? { replyTo: String(replyTo).slice(0, 64) } : {}),
  });

  // The full rendition, in its own document, keyed by the message it belongs
  // to. Best-effort by design — see the note above.
  if (image?.fullUrl) {
    setDoc(doc(db, 'chats', chatId, 'media', ref.id), {
      senderUid,
      url: image.fullUrl,
      w: image.fullWidth,
      h: image.fullHeight,
      createdAt: serverTimestamp(),
    }).catch(err => console.warn('[chat] full image not stored:', err?.code || err));
  }

  // Best-effort: the message is the thing that matters. A preview that lags by
  // one is a cosmetic problem; a message that failed to send is not.
  updateDoc(chatRef(chatId), {
    lastMessage: previewOf({ text: body, share: card, image }),
    lastMessageAt: serverTimestamp(),
    lastSenderUid: senderUid,
    [`unread.${recipientUid}`]: increment(1),
    // A thread the recipient had hidden comes back when someone writes in it.
    hiddenFor: [],
  }).then(() => { _unread = null; }).catch(err => console.warn('[chat] preview not updated:', err?.code || err));
}

// ── REACTIONS ────────────────────────────────────────────────────────────────
//
// A FIXED SET, mirrored in firestore.rules. The rules reject anything outside
// it, so the field cannot become a place to stash arbitrary strings on someone
// else's conversation — and every client renders the same five.
export const REACTIONS = ['👍', '❤️', '😂', '😮', '😢'];

/**
 * Adds, changes or clears MY reaction to a message.
 *
 * Stored as `reactions: { <uid>: <emoji> }` ON THE MESSAGE, not in a
 * subcollection. The message document is already streamed by the thread's
 * listener, so a reaction costs no extra read and no extra listener — and a
 * map keyed by uid is the shape the rules can police: "you may change your own
 * key and no one else's", exactly like `clearedAt` on the chat.
 *
 * `emoji: null` removes it, which is also what tapping your own reaction does.
 */
export async function setReaction(chatId, messageId, uid, emoji) {
  if (!chatId || !messageId || !uid) return;
  if (emoji !== null && !REACTIONS.includes(emoji)) throw new Error('bad-reaction');
  await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
    [`reactions.${uid}`]: emoji === null ? deleteField() : emoji,
  });
}

/**
 * Edits one of my own messages.
 *
 * `editedAt` is stamped so the UI can mark it. An edit that left no trace would
 * let someone quietly rewrite what the other person already replied to.
 */
export async function editMessage(chatId, messageId, text) {
  const body = trim(text, MESSAGE_MAX);
  if (!body) throw new Error('empty');
  await updateDoc(doc(db, 'chats', chatId, 'messages', messageId), {
    text: body,
    editedAt: serverTimestamp(),
  });
}

/**
 * Deletes one of my own messages.
 *
 * The list preview may now quote something that no longer exists, so the caller
 * passes what should replace it. Best-effort: losing the preview is cosmetic,
 * failing to delete the message is not.
 */
export async function deleteMessage(chatId, messageId, newPreview) {
  await deleteDoc(doc(db, 'chats', chatId, 'messages', messageId));
  if (newPreview !== undefined) {
    updateDoc(chatRef(chatId), {
      lastMessage: previewOf(newPreview),
      lastSenderUid: newPreview?.senderUid || '',
    }).catch(() => {});
  }
}

/**
 * Removes a conversation from MY list, and MY copy of its history with it.
 *
 * Not a document delete — see the note in firestore.rules. The chat belongs to
 * two people, and erasing it would take the other side's copy with it. Hiding
 * is per-user and reversible: if they write again, `sendMessage` clears the
 * flag and the thread returns, exactly as a messenger behaves.
 *
 * BUT THE THREAD MUST COME BACK EMPTY. The conversation id is derived from the
 * two uids, so there is only ever one document per pair — writing again reuses
 * it. Hiding alone therefore looked like deleting and behaved like archiving:
 * the thread returned with every old message still in it, which is not what
 * "delete" means to anyone.
 *
 * `clearedAt.<uid>` is that person's cutoff. Their message queries start after
 * it, so the history is gone for them and untouched for the other side — the
 * same asymmetry `hiddenFor` already has, and the only kind possible when one
 * document is shared by two people who each get to decide for themselves.
 */
/**
 * The full rendition of a sent picture, fetched when someone opens it.
 *
 * ONE READ, AND ONLY ON DEMAND. This is the other half of the split described
 * in sendMessage: the thread never touches this document, so a conversation
 * full of photos costs the preview bytes and nothing more until a picture is
 * actually opened.
 *
 * Returns null when there is no full rendition — an older message, or one
 * whose media write failed. The caller falls back to the preview it already
 * has, which is why that failure is survivable.
 */
export async function getMessageImage(chatId, messageId) {
  if (!chatId || !messageId) return null;
  try {
    const snap = await getDoc(doc(db, 'chats', chatId, 'media', messageId));
    return snap.exists() ? (snap.data().url || null) : null;
  } catch (err) {
    console.warn('[chat] full image unavailable:', err?.code || err);
    return null;
  }
}

export async function hideChat(chatId, uid) {
  if (!chatId || !uid) return;
  await updateDoc(chatRef(chatId), {
    hiddenFor: arrayUnion(uid),
    [`clearedAt.${uid}`]: serverTimestamp(),
  });
  _unread = null;
}

/**
 * This user's "history starts here" mark, or null if they never cleared it.
 *
 * Read once when a thread opens rather than watched: it only changes when the
 * user themselves deletes the conversation, which closes the thread anyway.
 */
export async function clearedCutoff(chatId, uid) {
  if (!chatId || !uid) return null;
  try {
    const snap = await getDoc(chatRef(chatId));
    return (snap.exists() && snap.data()?.clearedAt?.[uid]) || null;
  } catch {
    // Unreadable for any reason: show the thread rather than an empty one.
    return null;
  }
}

/** Clears MY unread counter. Called when a thread is on screen. */
export async function markChatRead(chatId, uid) {
  try {
    await updateDoc(chatRef(chatId), { [`unread.${uid}`]: 0 });
    _unread = null;          // the header badge is now wrong; make it re-ask
  } catch { /* not fatal — the badge simply lingers until next time */ }
}

// ── HEADER BADGE ─────────────────────────────────────────────────────────────
// How many unread messages I have, for the envelope in the site header.
//
// A ONE-SHOT QUERY, NOT A LISTENER, and this is deliberate. The header is
// rendered separately by every page, so it mounts afresh on each navigation —
// a listener there would re-read the whole chat list on every page view, which
// is precisely the pattern this app avoids elsewhere (see NotificationBell,
// which fetches once for the same reason).
//
// Cached briefly on top of that, so moving between pages costs nothing at all.
// The number can therefore lag by up to the TTL; opening Messages shows the
// live truth, and sending or reading a message clears the cache immediately.
const UNREAD_TTL_MS = 30 * 1000;
let _unread = null;          // { uid, total, at }

export async function fetchUnreadTotal(uid) {
  if (!uid) return 0;
  if (_unread && _unread.uid === uid && Date.now() - _unread.at < UNREAD_TTL_MS) {
    return _unread.total;
  }
  try {
    const snap = await getDocs(query(
      collection(db, 'chats'),
      where('participants', 'array-contains', uid),
      orderBy('lastMessageAt', 'desc'),
      limit(CHAT_LIST_MAX),
    ));
    const total = snap.docs.reduce((sum, d) => {
      const v = d.data();
      if ((v.hiddenFor || []).includes(uid)) return sum;   // hidden: not my problem
      return sum + (v.unread?.[uid] || 0);
    }, 0);
    _unread = { uid, total, at: Date.now() };
    return total;
  } catch {
    return _unread?.uid === uid ? _unread.total : 0;
  }
}

/**
 * LIVE messages, newest `max` only.
 *
 * Ordered DESCENDING because that is the only way to limit to the most recent
 * ones; the UI reverses for display. Returns the unsubscribe function — the
 * caller must call it (see useChat, which does so in its effect cleanup).
 */
export function subscribeMessages(chatId, cb, max = MESSAGE_PAGE, since = null) {
  if (!chatId) return () => {};
  // `since` is this user's clearedAt — see hideChat. Everything before it was
  // deleted by them and must stay invisible even though the documents live on
  // for the other participant.
  const parts = [orderBy('createdAt', 'desc'), limit(max)];
  if (since) parts.unshift(where('createdAt', '>', since));
  return onSnapshot(
    query(messagesRef(chatId), ...parts),
    (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse()),
    (err) => { console.error('[chat] messages subscribe failed:', err); cb([]); },
  );
}

/**
 * LIVE list of my conversations. One query, ordered by recency, bounded.
 * Needs a composite index (participants array-contains + lastMessageAt desc).
 */
export function subscribeChats(uid, cb, max = CHAT_LIST_MAX) {
  if (!uid) return () => {};
  return onSnapshot(
    query(
      collection(db, 'chats'),
      where('participants', 'array-contains', uid),
      orderBy('lastMessageAt', 'desc'),
      limit(max),
    ),
    (snap) => cb(
      snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        // Firestore has no 'not-array-contains', so hidden threads are filtered
        // here. The query is capped at 40, so this is a handful of rows.
        .filter(c => !(c.hiddenFor || []).includes(uid)),
    ),
    (err) => { console.error('[chat] list subscribe failed:', err); cb([]); },
  );
}

/**
 * One page of OLDER messages, fetched on demand. A one-shot read, never a
 * listener: history does not change, so watching it would be pure cost.
 */
export async function loadOlderMessages(chatId, oldestLoaded, max = MESSAGE_PAGE, since = null) {
  if (!chatId || !oldestLoaded?.createdAt) return [];
  try {
    const snap = await getDocs(query(
      messagesRef(chatId),
      orderBy('createdAt', 'desc'),
      where('createdAt', '<', oldestLoaded.createdAt),
      // Paging back must not walk past the cutoff into deleted history.
      ...(since ? [where('createdAt', '>', since)] : []),
      limit(max),
    ));
    return snap.docs.map(d => ({ id: d.id, ...d.data() })).reverse();
  } catch (err) {
    console.warn('[chat] older page failed:', err?.code || err);
    return [];
  }
}
